import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import * as defaultSkin from './skins/spinner.default.js';

/** @type {readonly ['sm', 'md', 'lg']} */
export const SPINNER_SIZES = Object.freeze(['sm', 'md', 'lg']);

/** @type {readonly ['neutral', 'primary', 'inverse']} */
export const SPINNER_TONES = Object.freeze(['neutral', 'primary', 'inverse']);

class RunSpinner extends skinResolutionMixin(RunElement) {
  #size = 'md';
  #tone = 'neutral';
  #label = 'Loading';
  #hiddenLabel = true;

  static defaultSkin = defaultSkin;

  static skinLoaders = {};

  static get observedAttributes() {
    return ['size', 'tone', 'label', 'hidden-label'];
  }

  connectedCallback() {
    super.connectedCallback();
    this.requestUpdate();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'size':  this.size = newValue; break;
      case 'tone':  this.tone = newValue; break;
      case 'label': this.label = newValue; break;
      case 'hidden-label':
        // Enumerated boolean: hidden-label="false" opts into the visible
        // label; any other present value (or absence) keeps the default true.
        this.hiddenLabel = newValue === null ? true : newValue !== 'false';
        break;
    }
  }

  get size() { return this.#size; }
  set size(value) {
    const coerced = String(value ?? '').trim();
    const resolved = SPINNER_SIZES.includes(coerced) ? coerced : 'md';
    if (this.#size === resolved) {
      if (this.getAttribute('size') !== resolved) this.setAttribute('size', resolved);
      return;
    }
    this.#size = resolved;
    this.setAttribute('size', resolved);
    this.requestUpdate();
  }

  get tone() { return this.#tone; }
  set tone(value) {
    const coerced = String(value ?? '').trim();
    const resolved = SPINNER_TONES.includes(coerced) ? coerced : 'neutral';
    if (this.#tone === resolved) {
      if (this.getAttribute('tone') !== resolved) this.setAttribute('tone', resolved);
      return;
    }
    this.#tone = resolved;
    this.setAttribute('tone', resolved);
    this.requestUpdate();
  }

  get label() { return this.#label; }
  set label(value) {
    const coerced = String(value ?? '').trim() || 'Loading';
    if (this.#label === coerced) return;
    this.#label = coerced;
    this.setAttribute('label', coerced);
    this.requestUpdate();
  }

  get hiddenLabel() { return this.#hiddenLabel; }
  set hiddenLabel(value) {
    const coerced = Boolean(value);
    if (this.#hiddenLabel === coerced) return;
    this.#hiddenLabel = coerced;
    this.setAttribute('hidden-label', String(coerced));
    this.requestUpdate();
  }

  // The skin renders the [role="status"] wrapper; the headless base owns the
  // accessible-name wiring on it after every render. When the label is
  // visible, the rendered text IS the accessible name — aria-label would
  // double-announce, so it is removed.
  updated(changedProperties) {
    super.updated?.(changedProperties);
    const wrapper = this.shadowRoot?.querySelector('[role="status"]');
    if (!wrapper) return;
    if (this.#hiddenLabel) {
      wrapper.setAttribute('aria-label', this.#label);
    } else {
      wrapper.removeAttribute('aria-label');
    }
  }
}

if (!customElements.get('run-spinner')) {
  customElements.define('run-spinner', RunSpinner);
}

export { RunSpinner };
export default RunSpinner;
